import { Button } from '@thanx/uikit/button'
import { Switch } from '@thanx/uikit/switch'
import { Text } from '@thanx/uikit/text'
import { useStyletron } from '@thanx/uikit/theme'
import { alert } from 'actions/flash'
import { giftCardProvidersApi } from 'api/giftCardProviders'
import { isError } from 'api/utils'
import giftCardPreview from 'assets/images/cms/gift-card-preview.png'
import giftCardsPreview from 'assets/images/cms/gift-cards-preview.png'
import { PermissionTooltip } from 'components/Navbar/Bar/PermissionTooltip'
import PageHeader from 'components/PageHeader'
import Spinner from 'components/Spinner'
import TabItem from 'components/Tabs/TabItem'
import TabNav from 'components/Tabs/TabNav'
import TabPane from 'components/Tabs/TabPane'
import useDispatch from 'hooks/useDispatch'
import { useGiftCardsAccess } from 'hooks/useGiftCardsAccess'
import { buildTranslate } from 'locales'
import React, { useCallback } from 'react'
import { Col, Container, Row } from 'react-bootstrap-five'
import Tab from 'react-bootstrap-five/Tab'
import Helmet from 'react-helmet'
import { useSelector } from 'react-redux'
import {
  GiftCardState,
  selectGiftCardProvider,
  selectSelectedProviderId,
  setSelectedProviderId,
} from 'slices/giftCards'
import { getUrlAddress } from 'utilities/content/linkUrls'
import EmptyState from './EmptyState'
import { getFileUploadsPayload, validateGiftCardProvider } from './helpers'

import ProviderConfiguration from './ProviderConfiguration'

const t = buildTranslate('gift_cards')

const Landing: React.FC = () => {
  const [css] = useStyletron()
  const dispatch = useDispatch()
  const { manageDisabled, manageDisabledReason } = useGiftCardsAccess()

  const [updateProvider, { isLoading: isPublishing }] =
    giftCardProvidersApi.useUpdateGiftCardProviderMutation()
  const { data, isLoading: isFetching } =
    giftCardProvidersApi.useGetGiftCardProvidersQuery()

  const giftCardProviders = data?.gift_card_providers || []
  const selectedProviderId = useSelector(selectSelectedProviderId)
  const selectedProviderConfig = useSelector((state: GiftCardState) =>
    selectGiftCardProvider(state, selectedProviderId)
  )
  const handlePublish = useCallback(async () => {
    if (!selectedProviderConfig) return

    const validationError = validateGiftCardProvider(selectedProviderConfig)
    if (validationError) {
      dispatch(
        alert({
          key: 'danger',
          message: t(validationError),
          timeout: 3,
        })
      )
      return
    }

    const fileUploads = getFileUploadsPayload(selectedProviderConfig)

    const result = await updateProvider({
      id: selectedProviderConfig.id,
      gift_card_provider: {
        card_image_type: selectedProviderConfig.cardImageType,
        external_link_enabled: selectedProviderConfig.externalLinkEnabled,
        card_background_color: selectedProviderConfig.cardBackgroundColor,
        external_link_url: getUrlAddress(
          selectedProviderConfig.externalLinkUrl
        ),
        file_uploads: fileUploads,
      },
    })

    if (isError(result)) {
      dispatch(
        alert({
          key: 'danger',
          message: t('alert_fail'),
          timeout: 3,
        })
      )
      return
    }

    dispatch(
      alert({
        key: 'success',
        message: t('alert_success'),
        timeout: 5,
      })
    )
  }, [dispatch, selectedProviderConfig, updateProvider])

  return (
    <>
      <Helmet>
        <title>{t('page_title')}</title>
      </Helmet>
      <PageHeader title={t('title')}>
        <Tab.Container
          activeKey={selectedProviderId ? selectedProviderId.toString() : ''}
          transition
          onSelect={tab => {
            if (!tab) return
            dispatch(setSelectedProviderId(parseInt(tab)))
          }}
        >
          <TabNav>
            {giftCardProviders.length > 1 &&
              giftCardProviders.map(provider => (
                <TabItem
                  key={provider.id}
                  state={provider.id.toString()}
                  title={provider.name}
                />
              ))}
          </TabNav>
          <div
            className={`grey-05-bkg border-top-1 pb-xxl grey-20-border ${css({
              minHeight: 'calc(100% - 75px)',
            })}`}
          >
            <Container className="py-xl">
              <Spinner
                isLoading={isFetching}
                size="3x"
                className={'d-flex justify-content-center'}
              >
                <Switch
                  condition={giftCardProviders.length > 0}
                  fallback={<EmptyState />}
                >
                  <Row>
                    <Col
                      md={9}
                      className={css({
                        paddingLeft: '86px',
                        paddingRight: '86px',
                      })}
                    >
                      <Tab.Content>
                        {giftCardProviders.map(provider => (
                          <TabPane
                            className={`gift-card-provider-tab ${css({
                              paddingTop: '0px !important',
                              paddingBottom: '0px !important',
                            })}`}
                            key={provider.id}
                            state={provider.id.toString()}
                          >
                            <ProviderConfiguration provider={provider} />
                          </TabPane>
                        ))}
                      </Tab.Content>
                      <div className="belize-hole-10-bkg p-m mb-m rounded">
                        <Row>
                          <Col md={8}>
                            <Text.BodyText4 color="belizeHole50">
                              <strong>{t('info_panel.ready_title')}</strong>{' '}
                              {t('info_panel.ready_description')}
                            </Text.BodyText4>
                          </Col>
                          <Col
                            md={4}
                            className="d-flex align-items-center justify-content-end"
                          >
                            <PermissionTooltip
                              disabled={manageDisabled}
                              reason={manageDisabledReason}
                              id="gift-cards-publish-inline"
                              placement="top"
                              inline
                            >
                              <Button
                                isLoading={isPublishing}
                                onClick={handlePublish}
                                type="button"
                                disabled={manageDisabled}
                              >
                                {t('publish')}
                              </Button>
                            </PermissionTooltip>
                          </Col>
                        </Row>
                      </div>
                    </Col>

                    <Col md={3}>
                      <div className={css({ width: '200px' })}>
                        <PermissionTooltip
                          disabled={manageDisabled}
                          reason={manageDisabledReason}
                          id="gift-cards-publish-sidebar"
                          placement="left"
                        >
                          <Button
                            className={`px-s ${css({
                              width: '200px',
                            })}`}
                            onClick={handlePublish}
                            type="button"
                            disabled={manageDisabled}
                          >
                            {t('publish')}
                          </Button>
                        </PermissionTooltip>

                        <div className="mb-l">
                          <img
                            className={`${css({
                              width: '200px',
                              borderRadius: '8px',
                              marginTop: '88px',
                              marginBottom: '88px',
                            })}`}
                            src={giftCardPreview}
                            alt="Gift card preview"
                          />
                          <img
                            className={css({
                              width: '200px',
                              borderRadius: '8px',
                            })}
                            src={giftCardsPreview}
                            alt="Gift cards preview"
                          />
                        </div>
                      </div>
                    </Col>
                  </Row>
                </Switch>
              </Spinner>
            </Container>
          </div>
        </Tab.Container>
      </PageHeader>
    </>
  )
}

export default Landing
